import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { DailyCountData, DashboardChartDataService } from '../../services/dashboard-chart-data.service';

@Component({
  selector: 'app-cleaning-schedules-chart',
  imports: [CommonModule, TranslateModule, ChartModule],
  templateUrl: './cleaning-schedules-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CleaningSchedulesChartComponent implements OnInit {
  private readonly chartDataService = inject(DashboardChartDataService);
  private readonly translateService = inject(TranslateService);

  protected readonly isLoading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly chartData = signal<any>(null);
  protected readonly chartOptions = signal<any>(null);

  ngOnInit() {
    this.loadChartData();
    this.initChartOptions();
  }

  private loadChartData() {
    this.isLoading.set(true);
    this.error.set(null);

    this.chartDataService.getCleaningSchedulesData().subscribe({
      next: (dailyData) => {
        this.setupChartData(dailyData);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.error.set(this.translateService.instant('dashboard.charts.cleaningSchedules.error'));
        this.isLoading.set(false);
        console.error('Error loading cleaning schedules chart data:', err);
      }
    });
  }

  private setupChartData(dailyData: DailyCountData[]) {
    const labels = dailyData.map((d) => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const counts = dailyData.map((d) => d.count);

    // Calculate linear regression for trend line
    const trendData = this.calculateLinearRegression(counts);

    this.chartData.set({
      labels: labels,
      datasets: [
        {
          label: this.translateService.instant('dashboard.charts.cleaningSchedules.datasetLabel'),
          data: counts,
          borderColor: '#0891b2', // cyan-600
          backgroundColor: 'rgba(8, 145, 178, 0.1)', // cyan-600 with opacity
          tension: 0.4,
          fill: true
        },
        {
          label: 'Trend',
          data: trendData,
          borderColor: '#059669', // emerald-600
          borderDash: [5, 5],
          borderWidth: 2,
          backgroundColor: 'transparent',
          pointRadius: 0,
          tension: 0
        }
      ]
    });
  }

  private calculateLinearRegression(data: number[]): number[] {
    const n = data.length;
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumXX = 0;

    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += data[i];
      sumXY += i * data[i];
      sumXX += i * i;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return data.map((_, i) => slope * i + intercept);
  }

  private initChartOptions() {
    this.chartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1.5,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value: any) {
              return Number.isInteger(value) ? value : '';
            }
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (context: any) {
              if (context.datasetIndex === 0) {
                return `${context.dataset.label}: ${context.parsed.y} schedule${context.parsed.y !== 1 ? 's' : ''}`;
              } else {
                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}`;
              }
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    });
  }
}
